// The account: a name and email for display, and every way in - linked accounts, passkeys, the
// mobile number. The server refuses to remove the last way in, and the page says so before it tries.
import { useState, type FormEvent } from 'react'
import { auth, errorMessage, formatDate, type UserInfo } from '../api'
import { useAuth } from '../auth'
import { SmsSignIn } from '../components/SignInMethods'
import { Badge, Card, ErrorText, Field, Icon, PageHeader, ProviderIcon, providerName } from '../components/Ui'
import { addPasskey, passkeyErrorMessage, passkeysSupported } from '../passkeys'

export function AccountPage() {
  const { user, providers, setUser } = useAuth()
  const [error, setError] = useState<string | null>(null)
  if (!user) return null

  const waysIn = user.identities.length + user.passkeys.length + (user.mobileVerified ? 1 : 0)

  async function run(action: () => Promise<UserInfo>) {
    setError(null)
    try {
      setUser(await action())
    } catch (err) {
      setError(passkeyErrorMessage(err) ?? errorMessage(err))
    }
  }

  return (
    <>
      <PageHeader
        title="Account"
        aside={<Badge tone={waysIn > 1 ? 'good' : 'warn'}>{waysIn === 1 ? '1 way in' : `${waysIn} ways in`}</Badge>}
        lead="Your name and email are what license owners see when they grant you access. Everything else on this page is a way to sign in: keep at least one, and preferably a passkey on each device you use."
      />
      <ErrorText error={error} />

      <Profile user={user} onSaved={setUser} onError={setError} />

      <Card
        title="Passkeys"
        lead="A passkey is a key pair kept by your device or password manager and unlocked with your fingerprint, face or PIN. It cannot be guessed, phished or reused elsewhere."
      >
        {user.passkeys.length === 0 ? (
          <p className="muted">
            You have no passkey yet. Add one below: the browser asks your device to make it, and from then on that device signs you in with a touch or a
            glance.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Added</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {user.passkeys.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className="identity">
                        <Icon name="key" size={16} /> {p.name}
                      </span>
                    </td>
                    <td>{formatDate(p.createdUtc)}</td>
                    <td>{formatDate(p.lastUsedUtc)}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="button button-small button-quiet"
                        disabled={waysIn <= 1}
                        title={waysIn <= 1 ? 'Your only way in' : undefined}
                        onClick={() => run(() => auth.removePasskey(p.id))}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {passkeysSupported() ? <AddPasskey onAdded={setUser} onError={setError} /> : <p className="muted">This browser does not support passkeys.</p>}
      </Card>

      <div className="grid-2">
        <Card title="Linked accounts" lead="A Google or Microsoft account you can sign in with. Linking one never shares your password with Relatude.License.">
          {user.identities.length === 0 ? (
            <p className="muted">No Google or Microsoft account is linked.</p>
          ) : (
            <ul className="user-list">
              {user.identities.map((i) => (
                <li key={i.id}>
                  <span className="identity">
                    <ProviderIcon provider={i.provider} />
                    <span>
                      {providerName(i.provider)}
                      {(i.email || i.displayName) && <span className="muted small"> · {i.email || i.displayName}</span>}
                    </span>
                    <span className="muted small">linked {formatDate(i.linkedUtc)}</span>
                  </span>
                  <button
                    type="button"
                    className="button button-small button-quiet"
                    disabled={waysIn <= 1}
                    title={waysIn <= 1 ? 'Your only way in' : undefined}
                    onClick={() => run(() => auth.unlinkIdentity(i.id))}
                  >
                    Unlink
                  </button>
                </li>
              ))}
            </ul>
          )}
          {(providers.google || providers.microsoft) && (
            <div className="row">
              {providers.google && (
                <a className="button" href={auth.externalUrl('google', '/account')}>
                  <ProviderIcon provider="google" /> Link Google
                </a>
              )}
              {providers.microsoft && (
                <a className="button" href={auth.externalUrl('microsoft', '/account')}>
                  <ProviderIcon provider="microsoft" /> Link Microsoft
                </a>
              )}
            </div>
          )}
        </Card>

        <Card title="Mobile number" lead="A verified number lets you sign in with a code by SMS, and is one of the two ways a license owner can find you to grant access.">
          <p>
            {user.mobile ? (
              <span className="identity">
                <Icon name="phone" size={16} />
                <strong>{user.mobile}</strong> {user.mobileVerified ? <Badge tone="good">Verified</Badge> : <Badge tone="warn">Not verified</Badge>}
              </span>
            ) : (
              <span className="muted">No number yet.</span>
            )}
          </p>
          <p className="muted small">Verifying a new number replaces the old one.</p>
          <MobileChange onChanged={setUser} onError={setError} />
        </Card>
      </div>
    </>
  )
}

function Profile({ user, onSaved, onError }: { user: UserInfo; onSaved: (u: UserInfo) => void; onError: (m: string | null) => void }) {
  const [name, setName] = useState(user.name)
  const [email, setEmail] = useState(user.email)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  async function save(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError(null)
    setSaved(false)
    try {
      onSaved(await auth.updateProfile(name, email))
      setSaved(true)
    } catch (err) {
      onError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Profile" lead={`Registered ${formatDate(user.createdUtc)}.`}>
      <form className="form form-inline" onSubmit={save}>
        <Field label="Name" hint="How you are shown, here and to license owners.">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
        </Field>
        <Field label="Email" hint="How a license owner can find you to grant access. Not used for signing in.">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <div className="row">
          <button className="button button-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {saved && (
            <span className="muted identity">
              <Icon name="check" size={16} /> Saved
            </span>
          )}
        </div>
      </form>
    </Card>
  )
}

function AddPasskey({ onAdded, onError }: { onAdded: (u: UserInfo) => void; onError: (m: string | null) => void }) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  async function add(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError(null)
    try {
      onAdded(await addPasskey(name || defaultName()))
      setName('')
    } catch (err) {
      onError(passkeyErrorMessage(err) ?? errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="row" onSubmit={add}>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={defaultName()} aria-label="Passkey name" />
      <button className="button button-primary" disabled={busy}>
        <Icon name="key" size={16} />
        {busy ? 'Waiting for the device…' : 'Add a passkey'}
      </button>
    </form>
  )
}

function defaultName(): string {
  const ua = navigator.userAgent
  const os = /iPhone|iPad/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'This device'
  return os + ' passkey'
}

function MobileChange({ onChanged, onError }: { onChanged: (u: UserInfo) => void; onError: (m: string | null) => void }) {
  const [open, setOpen] = useState(false)
  if (!open) {
    return (
      <div className="row">
        <button type="button" className="button" onClick={() => setOpen(true)}>
          <Icon name="phone" size={16} />
          Add or change number
        </button>
      </div>
    )
  }
  return (
    <div className="signin">
      <SmsSignIn
        verifyLabel="Verify"
        onBusy={() => {}}
        onError={onError}
        onSignedIn={(result) => {
          onChanged(result.user)
          setOpen(false)
        }}
      />
      <button type="button" className="button button-quiet button-small" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </div>
  )
}
